import React, { useState, useEffect } from 'react';
import './SecretAdminPanel.css';
import { fetchCategories, fetchItems, addItem, updateItem, deleteItem, migrateFromStaticData } from '../../services/adminService';
import { portfolioData as staticData } from '../Mars/data';

const SecretAdminPanel = ({ onClose }) => {
    const [isAuthenticated, setIsAuthenticated] = useState(false);
    const [password, setPassword] = useState('');
    const [categories, setCategories] = useState([]);
    const [selectedCategory, setSelectedCategory] = useState(null);
    const [items, setItems] = useState([]);
    const [loading, setLoading] = useState(false);
    const [editingItem, setEditingItem] = useState(null);

    // Form State
    const [formData, setFormData] = useState({
        title: '',
        description: '',
        image: '',
        tech: '', // comma separated
        links: '{}', // JSON string for flexibility
        // Specific fields can be added here
        type: '', // for sub-types
        stats: '{}',
        date: '',
        platform: '',
        buttonText: ''
    });

    useEffect(() => {
        const loadCats = async () => {
            const cats = await fetchCategories();
            setCategories(cats);
            if (cats.length > 0) setSelectedCategory(cats[0].id);
        };
        loadCats();
    }, []);

    useEffect(() => {
        if (selectedCategory && isAuthenticated) {
            loadItems(selectedCategory);
        }
    }, [selectedCategory, isAuthenticated]);

    const loadItems = async (catId) => {
        setLoading(true);
        try {
            const fetchedItems = await fetchItems(catId);
            setItems(fetchedItems);
        } catch (error) {
            console.error("Failed to load items", error);
        }
        setLoading(false);
    };

    const handleLogin = () => {
        if (password === "HimCode") {
            setIsAuthenticated(true);
        } else {
            alert("Access Denied");
        }
    };

    const handleInputChange = (e) => {
        const { name, value } = e.target;
        setFormData(prev => ({ ...prev, [name]: value }));
    };

    const resetForm = () => {
        setFormData({
            title: '',
            description: '',
            image: '',
            tech: '',
            links: '{}',
            type: '',
            stats: '{}',
            date: '',
            platform: '',
            buttonText: ''
        });
        setEditingItem(null);
    };

    const handleEdit = (item) => {
        setEditingItem(item);
        setFormData({
            title: item.title || '',
            description: item.description || '',
            image: item.image || '',
            tech: item.tech ? item.tech.join(', ') : '',
            links: JSON.stringify(item.links || {}, null, 2),
            type: item.type || '',
            stats: JSON.stringify(item.stats || {}, null, 2),
            date: item.date || '',
            platform: item.platform || '',
            buttonText: item.buttonText || ''
        });
    };

    const handleDelete = async (itemId) => {
        if (window.confirm("Are you sure you want to delete this item?")) {
            setLoading(true); // Optimistic update or wait? Let's wait.
            await deleteItem(selectedCategory, itemId);
            await loadItems(selectedCategory);
            setLoading(false);
        }
    };

    const handleMigration = async () => {
        if (window.confirm("WARNING: This will overwrite 'cms/portfolio_v1' with the content of 'data.js'. \n\nContinue?")) {
            setLoading(true);
            try {
                const res = await migrateFromStaticData(staticData);
                alert(`Migration Complete!\nSuccess: ${res.success}\nFailed: ${res.failed}`);
                await loadItems(selectedCategory);
            } catch (e) {
                alert("Migration Failed: " + e.message);
            }
            setLoading(false);
        }
    };

    const runDebugCheck = async () => {
        console.log("DEBUG: Checking Firestore...");
        try {
            const cats = await fetchCategories();
            for (const cat of cats) {
                const items = await fetchItems(cat.id);
                console.log(`DEBUG: Category ${cat.id} has ${items.length} items`);
                if (items.length > 0) {
                    alert(`✅ FOUND: ${items.length} items in '${cat.id}' (cms/portfolio_v1)`);
                }
            }
        } catch (e) {
            alert("Debug Error: " + e.message);
        }
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setLoading(true);

        const dataToSave = {
            ...formData,
            tech: formData.tech.split(',').map(t => t.trim()).filter(t => t),
            links: JSON.parse(formData.links || '{}'),
            stats: JSON.parse(formData.stats || '{}')
        };

        // Clean up empty fields
        Object.keys(dataToSave).forEach(key => {
            if (dataToSave[key] === '' || (Array.isArray(dataToSave[key]) && dataToSave[key].length === 0)) {
                delete dataToSave[key];
            }
        });

        try {
            if (editingItem) {
                await updateItem(selectedCategory, editingItem.id, dataToSave);
            } else {
                await addItem(selectedCategory, dataToSave);
            }
            resetForm();
            await loadItems(selectedCategory);
        } catch (error) {
            console.error("Save failed", error);
            alert("Failed to save: " + error.message);
        }
        setLoading(false);
    };

    if (!isAuthenticated) {
        return (
            <div className="admin-overlay">
                <div className="admin-login">
                    <h2>TOP SECRET CLEARANCE REQUIRED</h2>
                    <input
                        type="password"
                        value={password}
                        onChange={(e) => setPassword(e.target.value)}
                        placeholder="Enter Access Code"
                        onKeyPress={(e) => e.key === 'Enter' && handleLogin()}
                    />
                    <button onClick={handleLogin}>Authenticate</button>
                    <button className="close-btn" style={{ marginTop: '20px' }} onClick={onClose}>Abort</button>
                </div>
            </div>
        );
    }
    return (
        <div className="admin-overlay">
            <div className="admin-panel-container">
                <header className="admin-header">
                    <h2>MARS MISSION CONTROL API @ {selectedCategory}</h2>
                    <div style={{ display: 'flex', gap: '10px' }}>
                        <button className="close-btn" style={{ borderColor: '#0ff', color: '#0ff' }} onClick={runDebugCheck}>? Check DB</button>
                        <button className="close-btn" style={{ borderColor: '#ff0', color: '#ff0' }} onClick={handleMigration}>⚠ Initialize Database</button>
                        <button className="close-btn" onClick={onClose}>Exit Terminal</button>
                    </div>
                </header>

                <nav className="category-nav">
                    {categories.map(cat => (
                        <div
                            key={cat.id}
                            className={`category-tab ${selectedCategory === cat.id ? 'active' : ''}`}
                            onClick={() => { setSelectedCategory(cat.id); resetForm(); }}
                        >
                            {cat.title}
                        </div>
                    ))}
                </nav >

                <div className="content-area">
                    <div className="form-section">
                        <h3>{editingItem ? 'Update Manifest' : 'New Entry'}</h3>
                        <form onSubmit={handleSubmit}>
                            <div className="form-group">
                                <label>Title</label>
                                <input name="title" value={formData.title} onChange={handleInputChange} required />
                            </div>
                            <div className="form-group">
                                <label>Description</label>
                                <textarea name="description" value={formData.description} onChange={handleInputChange} rows={4} required />
                            </div>
                            <div className="form-group">
                                <label>Image URL</label>
                                <input name="image" value={formData.image} onChange={handleInputChange} />
                            </div>
                            <div className="form-group">
                                <label>Tech Stack (comma separated)</label>
                                <input name="tech" value={formData.tech} onChange={handleInputChange} />
                                <div className="tech-stack-preview">
                                    {formData.tech.split(',').filter(t => t.trim()).map((t, i) => (
                                        <span key={i} className="tech-tag">{t.trim()}</span>
                                    ))}
                                </div>
                            </div>
                            <div className="form-group">
                                <label>Links (JSON)</label>
                                <textarea
                                    name="links"
                                    value={formData.links}
                                    onChange={handleInputChange}
                                    rows={5}
                                    placeholder={`e.g.
{
  "playStore": "https://...",
  "code": "https://..."
}`}
                                />
                                <small style={{ color: '#888', display: 'block', marginTop: '5px' }}>
                                    Valid keys: playStore, code, demo, live
                                    <pre style={{ background: '#222', padding: '5px', borderRadius: '4px', color: '#aaffaa', fontSize: '0.85em', marginTop: '5px' }}>
                                        {`{
  "demo": "#",
  "code": "#"
}`}
                                    </pre>
                                </small>
                            </div>

                            {/* Conditional Extra Fields based on guess or generic */}
                            <div className="form-group">
                                <label>Type (e.g. app, ai, web)</label>
                                <input name="type" value={formData.type} onChange={handleInputChange} placeholder="app, ai, web, research, extension" />
                            </div>
                            <div className="form-group">
                                <label>Stats (JSON)</label>
                                <textarea
                                    name="stats"
                                    value={formData.stats}
                                    onChange={handleInputChange}
                                    rows={4}
                                    placeholder={`e.g.
{
  "downloads": "10k+",
  "rating": "4.5"
}`}
                                />
                                <small style={{ color: '#888', display: 'block', marginTop: '5px' }}>
                                    Example:
                                    <pre style={{ background: '#222', padding: '5px', borderRadius: '4px', color: '#aaffaa', fontSize: '0.85em', marginTop: '5px' }}>
                                        {`{
  "downloads": "10k+",
  "rating": "4.5"
}`}
                                    </pre>
                                </small>
                            </div>
                            <div className="form-group">
                                <label>Date / Platform / ButtonText</label>
                                <div style={{ display: 'flex', gap: '10px' }}>
                                    <input name="date" placeholder="Dec 2024" value={formData.date} onChange={handleInputChange} />
                                    <input name="platform" placeholder="Web / iOS" value={formData.platform} onChange={handleInputChange} />
                                    <input name="buttonText" placeholder="View / Download" value={formData.buttonText} onChange={handleInputChange} />
                                </div>
                                <small style={{ color: '#888', display: 'block', marginTop: '5px' }}>
                                    Examples: Date="2024", Platform="Android", ButtonText="Get it"
                                </small>
                            </div>

                            <div className="form-actions">
                                <button type="submit" className="save-btn" disabled={loading}>
                                    {loading ? 'Transmitting...' : (editingItem ? 'Update Data' : 'Upload Data')}
                                </button>
                                {editingItem && (
                                    <button type="button" className="cancel-btn" onClick={resetForm}>Cancel Edit</button>
                                )}
                            </div>
                        </form>
                    </div>

                    <div className="list-section">
                        <h3>Existing Records ({items.length})</h3>
                        <div className="items-list">
                            {loading ? <p>Scanning database...</p> : items.map(item => (
                                <div key={item.id} className="list-item">
                                    <div className="item-info">
                                        <h4>{item.title}</h4>
                                        <p>{item.description && item.description.substring(0, 50)}...</p>
                                    </div>
                                    <div className="item-actions">
                                        <button className="edit-btn" onClick={() => handleEdit(item)}>Edit</button>
                                        <button className="delete-btn" onClick={() => handleDelete(item.id)}>Del</button>
                                    </div>
                                </div>
                            ))}
                        </div>
                    </div>
                </div>
            </div >
        </div >
    );
};

export default SecretAdminPanel;
